import React, { useState } from 'react';
import { Button, Checkbox, Input, Modal, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { submitCustomEhr } from 'api/insuranceBilling';
import useEhrConnections from 'hooks/useEhrConnections';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import s from './s.module.less';

// 原型 PM_CALL_LINK：与 dashboard 底部「Book time with the Klarity billing team」同一个日程
const BILLING_TEAM_CALENDLY = 'https://calendly.com/d/dz59-5yf-pcn/guorui-tia-meeting-30-minutes?month=2026-09';

type FormProps = {
    onCancel: () => void;
    // 提交成功后回调（宿主把该 EHR 计入 billingEhrs——后端要求 custom 默认勾选）
    onSubmitted?: (ehrName: string) => void;
    // 点 Upcoming 卡片进来时预填 EHR 名称
    initialName?: string;
};

// "Tell us about your EHR"（未收录 / Upcoming EHR 登记）表单：EhrConnectModal 的内嵌视图与
// CustomEhrModal（settings EHR tab 的独立弹窗）共用同一份提交逻辑。
// 原型 V3：同时让 provider 把 agent 邮箱加进自己的 EHR；「I added this email」只做前端门禁，不落库
export function CustomEhrForm({ onCancel, onSubmitted, initialName = '' }: FormProps) {
    const [, , refreshConnections] = useEhrConnections();
    const [billingStatus] = useInsuranceBillingStatus();
    const agentEmail = billingStatus?.agentEmail || '';
    const [ehrName, setEhrName] = useState(initialName);
    const [loginUrl, setLoginUrl] = useState('');
    const [note, setNote] = useState('');
    const [agentAdded, setAgentAdded] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = !!ehrName.trim() && agentAdded;

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(agentEmail);
        message.success('Email copied to clipboard');
    };

    const handleSubmit = async () => {
        const name = ehrName.trim();
        if (!name) {
            message.warning('Please enter your EHR name');
            return;
        }
        setSubmitting(true);
        const res = await submitCustomEhr({
            ehrName: name,
            loginUrl: loginUrl.trim() || undefined,
            note: note.trim() || undefined,
        });
        setSubmitting(false);
        if (res && !res.error) {
            message.success(`Thanks! We will work on ${name} and reach out.`);
            // custom 也是一条真实连接（pending 等内部对接）：刷新后广播给所有挂载中的实例
            refreshConnections();
            onSubmitted?.(name);
        }
    };

    return (
        <>
            <div className={s.customBody}>
                <p className={s.customDesc}>
                    Enter the name of your EHR. We cannot build the integration without access, so add our agent account
                    to it now as well.
                </p>
                <div className={s.customLabel}>
                    EHR name<span className={s.customReq}>*</span>
                </div>
                <Input
                    size="large"
                    placeholder="e.g. Acme Health Records"
                    value={ehrName}
                    maxLength={100}
                    onChange={(e) => setEhrName(e.target.value)}
                />
                <div className={s.customLabel}>
                    Login URL<span className={s.customOpt}>Optional</span>
                </div>
                <Input
                    size="large"
                    placeholder="The address you sign in at, e.g. https://yourpractice.ehr.com"
                    value={loginUrl}
                    maxLength={500}
                    onChange={(e) => setLoginUrl(e.target.value)}
                />
                <div className={s.customLabel}>
                    Anything we should know?<span className={s.customOpt}>Optional</span>
                </div>
                <Input.TextArea
                    rows={3}
                    placeholder="e.g. which version you use, or who manages users at your practice"
                    value={note}
                    maxLength={1000}
                    onChange={(e) => setNote(e.target.value)}
                />
                <div className={s.agentInvite}>
                    <div className={s.agentInviteTitle}>Add Klarity&apos;s agent account to your EHR</div>
                    <p className={s.agentInviteText}>
                        This gives our team access to your EHR so we can build the integration. Adding the account does
                        not sync any data yet; nothing is read until the integration is ready.
                    </p>
                    <ol className={s.agentInviteSteps}>
                        <li>In your EHR, invite a new team member with the email below.</li>
                        <li>Give it access to visits, notes and billing.</li>
                        <li>Leave two-factor authentication off for this account.</li>
                    </ol>
                    <div className={s.guideEmailRow}>
                        <Input value={agentEmail} readOnly className={`${s.guideEmailInput} ${s.agentEmailInput}`} />
                        <Button icon={<CopyOutlined />} onClick={handleCopyEmail} className={s.agentCopyBtn}>
                            Copy
                        </Button>
                    </div>
                    <Checkbox
                        className={s.agentAdded}
                        checked={agentAdded}
                        onChange={(e) => setAgentAdded(e.target.checked)}
                    >
                        I added this email as a user in my EHR
                    </Checkbox>
                    <a
                        className={s.agentInviteCall}
                        href={BILLING_TEAM_CALENDLY}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Book 15 minutes with our billing team ›
                    </a>
                </div>
            </div>
            <div className={s.customFoot}>
                <Button size="large" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="primary" size="large" onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
                    Submit
                </Button>
            </div>
        </>
    );
}

type ModalProps = {
    open: boolean;
    onClose: () => void;
    onSubmitted?: (ehrName: string) => void;
    initialName?: string;
};

// 独立弹窗版（settings 的 EHR tab 用；picker 里是内嵌视图切换，不走这里）
export function CustomEhrModal({ open, onClose, onSubmitted, initialName }: ModalProps) {
    return (
        <Modal
            open={open}
            onCancel={onClose}
            title="Tell us about your EHR"
            footer={null}
            width={640}
            destroyOnClose
            className={s.customModal}
        >
            <CustomEhrForm onCancel={onClose} onSubmitted={onSubmitted} initialName={initialName} />
        </Modal>
    );
}
